import {
  ACCEPT_ALL,
  REJECT_ALL,
  getConsent,
  bannerReject,
  onConsentChange,
  saveConsent,
  type Choices,
  type Purpose,
} from './consent'

/**
 * The consent banner and the Privacy choices dialog. Plain DOM in a shadow
 * root, so the same code runs on the static marketing pages, the editor and
 * the builder, and page styles can't reach it.
 *
 * Rules it follows (GDPR Art. 7, EDPB Guidelines 05/2020 and 03/2022, and
 * the Czech DPA's cookie guidance): refusing is as easy as accepting (same
 * layer, same look), nothing optional is on before a yes, the page stays
 * usable without answering, and the choice can be changed at any time from
 * a link on every page.
 */

/** Whether product analytics is set up, so there is something to ask about. */
export type Offer = { analytics: boolean }
export type ChoiceSource = 'banner' | 'dialog'

const HOST_ID = 'easypatt-privacy'
const POLICY_URL = '/privacy.html'
const OPEN_HASH = '#privacy-choices'
const AVATAR_URL = '/pavol.webp'

let offer: Offer = { analytics: false }
let root: ShadowRoot | null = null
let banner: HTMLElement | null = null
let dialog: HTMLDialogElement | null = null
let onChoice: ((choices: Choices, via: ChoiceSource) => void) | null = null

export function mountPrivacyUi(
  what: Offer,
  onChoiceMade?: (choices: Choices, via: ChoiceSource) => void,
): void {
  offer = what
  onChoice = onChoiceMade ?? null
  document.addEventListener('click', onDocumentClick)
  onConsentChange((state) => {
    if (state.decided) hideBanner()
  })
  const openFromHash = () => {
    if (window.location.hash !== OPEN_HASH) return
    history.replaceState(null, '', window.location.pathname + window.location.search)
    openPrivacyChoices()
  }
  window.addEventListener('hashchange', openFromHash)
  const start = () => {
    // Development only: ?show-banner shows it whatever this browser already
    // decided. Brave sends Global Privacy Control, which otherwise hides it.
    const preview = import.meta.env.DEV && new URLSearchParams(window.location.search).has('show-banner')
    if ((preview || !getConsent().decided) && offer.analytics) showBanner()
    openFromHash()
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true })
  else start()
}

/** For "Privacy choices" buttons. Any element with data-privacy-choices, or
 *  a link to #privacy-choices, opens it without extra wiring. */
export function openPrivacyChoices(): void {
  const d = ensureDialog()
  const { choices } = getConsent()
  for (const input of d.querySelectorAll<HTMLInputElement>('input[data-purpose]')) {
    input.checked = choices[input.dataset.purpose as Purpose]
  }
  if (!d.open) d.showModal()
}

function onDocumentClick(e: MouseEvent): void {
  const target = e.target as Element | null
  const trigger = target?.closest?.('[data-privacy-choices], a[href$="#privacy-choices"]')
  if (!trigger) return
  e.preventDefault()
  openPrivacyChoices()
}

function choose(choices: Choices, via: ChoiceSource): void {
  const final: Choices = { anonStats: choices.anonStats, analytics: offer.analytics && choices.analytics }
  saveConsent(final)
  onChoice?.(final, via)
}

// ---- Banner ----------------------------------------------------------------

function showBanner(): void {
  if (banner) return
  const r = ensureRoot()
  banner = el('section', { class: 'banner', role: 'region', 'aria-label': 'Privacy choice' })
  banner.append(
    el(
      'div',
      { class: 'head' },
      // The text says who it is, so the photo is decoration for screen readers.
      el('img', { class: 'avatar', src: AVATAR_URL, alt: '', width: '44', height: '44', decoding: 'async' }),
      el('p', { class: 'title' }, 'Hi, maker!', waveIcon()),
    ),
    // Personal, but neutral: no guilt or pressure to say yes (EDPB
    // Guidelines 03/2022 on deceptive design), and saying no costs nothing.
    el(
      'p',
      { class: 'text' },
      'Pavol here, leather maker and founder of EasyPatt. Your consent helps me improve the app. ' +
        'Visits are counted anonymously, without cookies. If you allow it, I’ll also remember this ' +
        'browser to see what you come back to. Your patterns are never included, and nothing changes ' +
        'if you say no.',
    ),
    actions([
      button('Don’t allow', () => choose(bannerReject(getConsent().choices), 'banner')),
      button('Allow', () => choose(ACCEPT_ALL, 'banner')),
    ]),
    el(
      'div',
      { class: 'links' },
      linkButton('Choose what to allow', openPrivacyChoices),
      policyLink(),
    ),
  )
  r.append(banner)
}

/** Lucide's hand (ISC, the editor's icon set) with two motion lines, drawn
 *  in the brand colour; an emoji would look different on every system. */
function waveIcon(): HTMLElement {
  const span = el('span', { class: 'wave', 'aria-hidden': 'true' })
  span.innerHTML =
    '<svg viewBox="-1 -3 28 28" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"/>' +
    '<path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"/>' +
    '<path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"/>' +
    '<path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>' +
    '<path d="M21.6 1.2a4.5 4.5 0 0 1 2.2 2.9"/>' +
    '<path d="M22.9 -1.6a7.5 7.5 0 0 1 3.4 4.7"/>' +
    '</svg>'
  return span
}

function hideBanner(): void {
  banner?.remove()
  banner = null
}

// ---- Privacy choices dialog ------------------------------------------------

type Row = { purpose: Purpose | null; title: string; text: string }

function rows(): Row[] {
  const out: Row[] = [
    {
      purpose: null,
      title: 'Essential',
      text: 'Keeps you signed in, keeps your work saved in this browser and remembers these choices.',
    },
    {
      purpose: 'anonStats',
      title: 'Anonymous statistics',
      text:
        'Counts page visits and which tools get used, with no cookies and no ID stored on your device. ' +
        'A visit can’t be linked to you, or to your visits on other days.',
    },
  ]
  if (offer.analytics) {
    out.push({
      purpose: 'analytics',
      title: 'Product analytics',
      text:
        'Stores a random ID in this browser so we can see which features people return to and where they ' +
        'get stuck. When you’re signed in it’s tied to your account ID, never your email.',
    })
  }
  return out
}

function ensureDialog(): HTMLDialogElement {
  if (dialog) return dialog
  const r = ensureRoot()
  const d = el('dialog', { class: 'dialog', 'aria-labelledby': 'pc-title' }) as HTMLDialogElement
  const body = el('div', { class: 'd-body' })
  const close = el('button', { class: 'close', type: 'button', 'aria-label': 'Close' }, '×')
  close.addEventListener('click', () => d.close())
  body.append(
    close,
    el('h2', { id: 'pc-title' }, 'Privacy choices'),
    el(
      'p',
      { class: 'intro' },
      'Choose what EasyPatt may collect in this browser. You can come back here any time from ' +
        '“Privacy choices” at the bottom of every page or in the app’s Help.',
    ),
  )
  for (const row of rows()) {
    const text = el('div', { class: 'row-copy' }, el('p', { class: 'row-title' }, row.title), el('p', { class: 'row-text' }, row.text))
    if (!row.purpose) {
      body.append(el('div', { class: 'row' }, text, el('span', { class: 'always' }, 'Always on')))
      continue
    }
    const id = `pc-${row.purpose}`
    const input = el('input', {
      class: 'switch',
      type: 'checkbox',
      role: 'switch',
      id,
      'data-purpose': row.purpose,
    }) as HTMLInputElement
    const label = el('label', { class: 'row', for: id }, text, input)
    body.append(label)
  }
  const read = (): Choices => {
    const c: Choices = { ...REJECT_ALL }
    for (const input of d.querySelectorAll<HTMLInputElement>('input[data-purpose]')) {
      c[input.dataset.purpose as Purpose] = input.checked
    }
    return c
  }
  const done = (c: Choices) => {
    choose(c, 'dialog')
    d.close()
  }
  const footer = actions(
    [
      button('Reject all', () => done(REJECT_ALL)),
      button('Accept all', () => done(ACCEPT_ALL)),
      button('Save choices', () => done(read()), 'save'),
    ],
    'd-actions',
  )
  d.append(body, footer, el('p', { class: 'd-links' }, policyLink()))
  d.addEventListener('click', (e) => {
    if (e.target === d) d.close()
  })
  r.append(d)
  dialog = d
  return d
}

// ---- DOM helpers -----------------------------------------------------------

function ensureRoot(): ShadowRoot {
  if (root) return root
  const host = document.createElement('div')
  host.id = HOST_ID
  document.body.append(host)
  root = host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = CSS
  root.append(style)
  return root
}

function el(tag: string, attrs: Record<string, string> = {}, ...children: Array<Node | string>): HTMLElement {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v)
  node.append(...children)
  return node
}

function button(label: string, onClick: () => void, extraClass = ''): HTMLElement {
  const b = el('button', { type: 'button', class: `btn ${extraClass}`.trim() }, label)
  b.addEventListener('click', onClick)
  return b
}

function linkButton(label: string, onClick: () => void): HTMLElement {
  const b = el('button', { type: 'button', class: 'link' }, label)
  b.addEventListener('click', onClick)
  return b
}

function policyLink(): HTMLElement {
  return el('a', { class: 'link', href: POLICY_URL, target: '_blank', rel: 'noopener' }, 'Privacy policy')
}

function actions(buttons: HTMLElement[], cls = 'actions'): HTMLElement {
  return el('div', { class: cls }, ...buttons)
}

const CSS = `
:host { all: initial; }
* { box-sizing: border-box; }
.banner, .dialog {
  --bg: #ffffff; --ink: #1f2937; --muted: #5b6472; --line: #e5e7eb;
  --btn-bg: #1f2937; --btn-ink: #ffffff; --btn-hover: #0f172a;
  --brand: #1b96d1; --switch-off: #cbd2db;
  font: 14px/1.5 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--ink); background: var(--bg);
  -webkit-font-smoothing: antialiased;
}
@media (prefers-color-scheme: dark) {
  .banner, .dialog {
    --bg: #1e293b; --ink: #f3f4f6; --muted: #aab4c3; --line: #334155;
    --btn-bg: #f3f4f6; --btn-ink: #0f172a; --btn-hover: #ffffff;
    --brand: #4fb3e3; --switch-off: #52617a;
  }
}
/* Bottom right: on the home page the left side holds the main call to action. */
.banner {
  position: fixed; right: 16px; bottom: 16px; z-index: 2147483000;
  width: min(420px, calc(100vw - 32px));
  border: 1px solid var(--line); border-radius: 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08);
  padding: 16px 16px 12px;
}
@media (prefers-reduced-motion: no-preference) {
  .banner { animation: rise 0.22s ease-out; }
  @keyframes rise { from { transform: translateY(10px); opacity: 0; } }
}
@media (max-width: 520px) {
  .banner { left: 8px; right: 8px; bottom: 8px; width: auto; padding: 14px 14px 10px; }
}
.head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.avatar {
  flex: none; width: 44px; height: 44px; border-radius: 50%; object-fit: cover;
  background: var(--line); box-shadow: 0 0 0 1px var(--line);
}
.title { margin: 0; font-weight: 650; font-size: 16px; }
.title { display: flex; align-items: center; gap: 6px; }
.wave { display: inline-flex; color: var(--brand); transform-origin: 45% 85%; }
/* Tilted as if caught mid-wave; the wave animation turns the wrapper. */
.wave svg { transform: rotate(20deg); }
@media (prefers-reduced-motion: no-preference) {
  .wave { animation: wave 1.8s ease-in-out 0.35s 2; }
  @keyframes wave {
    0%, 60%, 100% { transform: rotate(0deg); }
    10%, 30% { transform: rotate(14deg); }
    20% { transform: rotate(-8deg); }
    40% { transform: rotate(-4deg); }
    50% { transform: rotate(10deg); }
  }
}
.text { margin: 0 0 12px; color: var(--muted); font-size: 13.5px; }
.actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.btn {
  appearance: none; border: 0; border-radius: 9px; padding: 9px 12px; min-height: 40px;
  font: inherit; font-weight: 600; cursor: pointer;
  background: var(--btn-bg); color: var(--btn-ink);
}
.btn:hover { background: var(--btn-hover); }
.btn:focus-visible, .link:focus-visible, .switch:focus-visible, .close:focus-visible {
  outline: 2px solid var(--brand); outline-offset: 2px;
}
.links { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 10px; font-size: 13px; }
.link {
  appearance: none; background: none; border: 0; padding: 0; font: inherit; cursor: pointer;
  color: var(--ink); text-decoration: underline; text-underline-offset: 2px;
}
.dialog {
  border: 1px solid var(--line); border-radius: 16px; padding: 0;
  width: min(540px, calc(100vw - 24px)); max-height: calc(100dvh - 24px);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
}
.dialog[open] { display: flex; flex-direction: column; }
.dialog::backdrop { background: rgba(15, 23, 42, 0.45); }
.d-body { position: relative; padding: 20px 20px 4px; overflow-y: auto; }
.close {
  position: absolute; top: 10px; right: 10px; width: 32px; height: 32px;
  appearance: none; border: 0; border-radius: 8px; background: none; cursor: pointer;
  color: var(--muted); font: 22px/1 system-ui, sans-serif;
}
.close:hover { color: var(--ink); background: color-mix(in srgb, var(--line) 60%, transparent); }
h2 { margin: 0 40px 6px 0; font-size: 18px; font-weight: 650; }
.intro { margin: 0 0 8px; color: var(--muted); font-size: 13.5px; }
.row {
  display: flex; gap: 16px; align-items: flex-start; justify-content: space-between;
  padding: 12px 0; border-top: 1px solid var(--line);
}
label.row { cursor: pointer; }
.row-title { margin: 0 0 2px; font-weight: 600; }
.row-text { margin: 0; color: var(--muted); font-size: 13px; }
.always { flex: none; padding-top: 2px; font-size: 12.5px; color: var(--muted); white-space: nowrap; }
.switch {
  appearance: none; flex: none; position: relative; margin: 2px 0 0; cursor: pointer;
  width: 40px; height: 24px; border-radius: 999px; background: var(--switch-off);
  transition: background 0.15s;
}
.switch::after {
  content: ""; position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%;
  background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: transform 0.15s;
}
.switch:checked { background: var(--brand); }
.switch:checked::after { transform: translateX(16px); }
.d-actions {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  padding: 12px 20px; border-top: 1px solid var(--line);
}
@media (max-width: 520px) {
  .d-actions { grid-template-columns: 1fr 1fr; }
  .d-actions .save { grid-column: 1 / -1; }
}
.d-links { margin: 0; padding: 0 20px 14px; font-size: 13px; }
`
